import React, { useState, useEffect } from 'react'
import { DatePicker, Spin, Button, Select, Alert, message } from 'antd'
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import store from 'store'
import queryString from 'query-string'
import { getSewingProductivityByPartner, listPartner } from '../../services/api/3dApi'

const { RangePicker } = DatePicker

const GROUP_COLORS = {
    binh_minh: '#DCE6F1',
    vendor: '#fff3e0',
    summary: '#ede7f6',
}

const CRITERIA_ROWS = [
    { label: 'Nhân sự', field: 'staff_count', isTotal: true },
    { label: 'Năng suất', field: 'productivity', isTotal: false, format: 'percent' },
    { label: 'Thực tế', field: 'actual', isTotal: true },
    { label: 'Định mức theo năng suất', field: 'quota_by_productivity', isTotal: true },
    { label: 'Tỷ lệ thực tế / định mức', field: 'rate', isTotal: true, format: 'percent' },
]

const formatVal = (val, fmt) => {
    if (val === null || val === undefined) return '-'
    if (fmt === 'percent') return `${Number(val).toFixed(1)}%`
    return Number(val).toLocaleString('en-US')
}

const buildColumns = (groups) => {
    const cols = []
    groups.forEach((group) => {
        group.partners.forEach((partner) => {
            const groupNames =
                partner.groups && partner.groups.length > 0
                    ? partner.groups.map((g) => g.name).join('+')
                    : null
            cols.push({
                key: `${group.partner_group}_${partner.partner_id}_${cols.length}`,
                label: groupNames ? `${partner.partner_name} (${groupNames})` : partner.partner_name,
                groupKey: group.partner_group,
                groupLabel: group.label,
                data: partner,
            })
        })
    })
    return cols
}

function SewingProductivityPage() {
    const history = useHistory()
    const [sewingDateRange, setSewingDateRange] = useState([moment(), moment()])
    const [listPrintPartner, setListPrintPartner] = useState([])
    const [printPartner, setPrintPartner] = useState([])
    const [loading, setLoading] = useState(false)
    const [data, setData] = useState(null)

    const fetchListPrintPartner = async () => {
        try {
            const query = queryString.stringify({ limit: 10000, page: 1, hidden: false, type: 'print_partner' })
            const { data: response } = await listPartner(query)
            const { data: resData, success, error } = response
            if (!success) throw new Error(error)
            const { partners = [] } = resData || {}
            setListPrintPartner(Array.isArray(partners) ? partners.map((i) => ({ label: i.name, value: i._id })) : [])
        } catch (e) {
            message.error(e.message)
        }
    }

    const fetchData = async (range, printPartnerIds) => {
        const [from, to] = range || []
        if (!from || !to) {
            setData(null)
            return
        }
        setLoading(true)
        setData(null)
        try {
            const payload = {
                sewing_date: {
                    from: from.format('YYYY-MM-DD'),
                    to: to.format('YYYY-MM-DD'),
                },
            }
            if (Array.isArray(printPartnerIds) && printPartnerIds.length > 0) {
                payload.print_partner = printPartnerIds
            }
            const { data: res } = await getSewingProductivityByPartner(payload)
            if (!res.success) throw new Error(res.message || res.error || 'Lỗi tải dữ liệu')
            setData(res.data)
        } catch (e) {
            message.error(e.message)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchListPrintPartner()
    }, [])

    useEffect(() => {
        fetchData(sewingDateRange, printPartner)
    }, [sewingDateRange, printPartner])

    const handleBackToDashboard = () => {
        store.set('3d_dashboard', 'ProductionStatisticsBySewingPartner')
        history.push('/dashboard')
    }

    const handleExportCsv = () => {
        if (!data) return
        const { groups = [], grand_total } = data
        const cols = buildColumns(groups)
        const summaryGroups = groups.map((g) => ({ key: g.partner_group, label: g.label, total: g.total }))

        const formatValCsv = (val, fmt) => {
            if (val === null || val === undefined) return '-'
            if (fmt === 'percent') return Number(val).toFixed(1)
            return Number(val)
        }

        const escapeCell = (val) => {
            const str = String(val ?? '')
            return str.includes(',') || str.includes('"') || str.includes('\n')
                ? `"${str.replace(/"/g, '""')}"`
                : str
        }

        const rows = []

        const header1 = ['TIÊU CHÍ']
        groups.forEach((g) => {
            g.partners.forEach((_, i) => header1.push(i === 0 ? g.label : ''))
        })
        summaryGroups.forEach((sg) => header1.push(sg.label))
        header1.push('TỔNG')
        rows.push(header1.map(escapeCell).join(','))

        const header2 = ['']
        cols.forEach((col) => header2.push(col.label))
        summaryGroups.forEach(() => header2.push(''))
        header2.push('')
        rows.push(header2.map(escapeCell).join(','))

        CRITERIA_ROWS.forEach((row) => {
            const cells = [row.label]
            cols.forEach((col) => cells.push(formatValCsv(col.data[row.field], row.format)))
            summaryGroups.forEach((sg) =>
                cells.push(row.isTotal ? formatValCsv(sg.total?.[row.field], row.format) : '-')
            )
            cells.push(row.isTotal ? formatValCsv(grand_total?.[row.field], row.format) : '-')
            rows.push(cells.map(escapeCell).join(','))
        })

        const csvContent = '﻿' + rows.join('\n')
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const [fromDate, toDate] = sewingDateRange || []
        const dateLabel = fromDate.isSame(toDate, 'day')
            ? fromDate.format('DD-MM-YYYY')
            : `${fromDate.format('DD-MM-YYYY')}_${toDate.format('DD-MM-YYYY')}`
        link.download = `nang-suat-may_${dateLabel}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    const renderTable = () => {
        if (!data || !data.groups || !data.groups.length) return null
        const { groups = [], grand_total } = data
        const cols = buildColumns(groups)
        const groupSpans = groups.map((group) => ({ ...group, span: group.partners.length }))
        const summaryGroups = groups.map((g) => ({ key: g.partner_group, label: g.label, total: g.total }))

        return (
            <div style={{ overflowX: 'auto' }} className="SewingProductivityTable">
                <style>{`.SewingProductivityTable .table-bordered td, .SewingProductivityTable .table-bordered th { border-color: #212121 !important; border-bottom-width: 1px !important; }`}</style>
                <table className="table table-bordered" style={{ minWidth: 800, fontSize: 13 }}>
                    <thead>
                        <tr>
                            <th
                                rowSpan={2}
                                style={{ verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap', background: '#93c47d', fontWeight: 700 }}
                            >
                                TIÊU CHÍ
                            </th>
                            {groupSpans.map((group) => (
                                <th
                                    key={group.partner_group}
                                    colSpan={group.span}
                                    style={{
                                        background: GROUP_COLORS[group.partner_group] || '#f5f5f5',
                                        textAlign: 'center',
                                        fontWeight: 700,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {group.label}
                                </th>
                            ))}
                            {summaryGroups.map((sg) => (
                                <th
                                    key={sg.key}
                                    rowSpan={2}
                                    style={{
                                        background: GROUP_COLORS.summary,
                                        textAlign: 'center',
                                        fontWeight: 700,
                                        whiteSpace: 'nowrap',
                                        verticalAlign: 'middle',
                                    }}
                                >
                                    {sg.label}
                                </th>
                            ))}
                            <th
                                rowSpan={2}
                                style={{
                                    background: GROUP_COLORS.summary,
                                    textAlign: 'center',
                                    fontWeight: 700,
                                    whiteSpace: 'nowrap',
                                    verticalAlign: 'middle',
                                }}
                            >
                                TỔNG
                            </th>
                        </tr>
                        <tr>
                            {cols.map((col) => {
                                const isWrappable = col.groupKey === 'binh_minh' || col.groupKey === 'vendor'
                                return (
                                    <th
                                        key={col.key}
                                        style={{
                                            background: GROUP_COLORS[col.groupKey] || '#f5f5f5',
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            ...(isWrappable
                                                ? { maxWidth: 140, whiteSpace: 'normal', wordBreak: 'break-word' }
                                                : { whiteSpace: 'nowrap' }),
                                        }}
                                    >
                                        {col.label}
                                    </th>
                                )
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {CRITERIA_ROWS.map((row) => (
                            <tr key={row.field}>
                                <td style={{ whiteSpace: 'nowrap', background: '#93c47d', fontWeight: 700 }}>{row.label}</td>
                                {cols.map((col) => (
                                    <td key={col.key} style={{ textAlign: 'center' }}>
                                        {formatVal(col.data[row.field], row.format)}
                                    </td>
                                ))}
                                {summaryGroups.map((sg) => (
                                    <td key={sg.key} style={{ textAlign: 'center' }}>
                                        {row.isTotal ? formatVal(sg.total?.[row.field], row.format) : '-'}
                                    </td>
                                ))}
                                <td style={{ textAlign: 'center' }}>
                                    {row.isTotal ? formatVal(grand_total?.[row.field], row.format) : '-'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )
    }

    return (
        <div className="SewingProductivityPage">
            <div className="container-fluid min-vh-100">
                <div className="col-12 mx-auto bg-white shadow-sm py-3">
                    <div className="mb-2">
                        <Button type="link" onClick={handleBackToDashboard} style={{ padding: 0 }}>
                            ← Thống kê sản xuất theo partner may
                        </Button>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                        <h3 className="mb-0">Thống kê năng suất may</h3>
                        <Button
                            icon={<i className="fa fa-download mr-1" />}
                            onClick={handleExportCsv}
                            disabled={!data || loading}
                        >
                            Export
                        </Button>
                    </div>
                    <div className="jumbotron px-3 pb-3 pt-3 mb-5">
                        <div className="d-flex align-items-center flex-wrap">
                            <span className="mr-2" style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>Ngày giao may:</span>
                            <RangePicker
                                value={sewingDateRange}
                                format="DD/MM/YYYY"
                                onChange={(dates) => {
                                    if (Array.isArray(dates) && dates.length === 2 && dates[0] && dates[1]) {
                                        setSewingDateRange(dates)
                                    }
                                }}
                                allowClear={false}
                                ranges={{
                                    'Today': [moment(), moment()],
                                    'Last 7 Days': [moment().subtract(6, 'day'), moment()],
                                    'This Month': [moment().startOf('month'), moment().endOf('month')],
                                }}
                            />
                            <span className="ml-4 mr-2" style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>Đối tác in ép:</span>
                            <Select
                                value={printPartner}
                                mode="multiple"
                                placeholder="--Chọn đối tác in ép--"
                                style={{ minWidth: 220 }}
                                allowClear
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.props?.children || '').toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                                onChange={setPrintPartner}
                            >
                                {listPrintPartner.map((i) => (
                                    <Select.Option key={`printPartner_${i.value}`} value={i.value}>{i.label}</Select.Option>
                                ))}
                            </Select>
                        </div>
                        {printPartner.length > 0 && (
                            <Alert
                                className="mt-3"
                                type="warning"
                                showIcon
                                message="Đang lọc theo đối tác in ép"
                                description="Số lượng thực tế chỉ tính các lô của đối tác in ép đã chọn, trong khi định mức đang được thiết lập theo nhóm may. Vì vậy chỉ số Tỷ lệ thực tế / định mức sẽ không đúng (thấp hơn thực tế), chỉ nên dùng để tham khảo."
                            />
                        )}
                    </div>
                    <Spin tip="Loading..." spinning={loading}>
                        {renderTable()}
                        {!loading && (!data || !data.groups || !data.groups.length) && (
                            <div className="text-center text-muted py-3">Không có dữ liệu</div>
                        )}
                    </Spin>
                </div>
            </div>
        </div>
    )
}

export default SewingProductivityPage
